import { useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './lib/auth.js';
import { api } from './lib/api.js';
import { AppShell } from './components/layout/AppShell.js';
import { Loading, EmptyState, Button,
  IconBuilding, IconCompass,
} from './components/ui/index.js';
import {
  LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage, AcceptInvitePage,
} from './pages/auth/AuthPages.js';
import { DashboardPage } from './pages/Dashboard.js';
import { BlogListPage } from './pages/blogs/BlogList.js';
import { BlogDetailPage } from './pages/blogs/BlogDetail.js';
import { PostEditorPage } from './pages/posts/PostEditor.js';
import { WidgetListPage } from './pages/widgets/WidgetList.js';
import { WidgetBuilderPage } from './pages/widgets/WidgetBuilder.js';
import { MediaLibraryPage } from './pages/media/MediaLibrary.js';
import { AnalyticsPage } from './pages/Analytics.js';
import { AuditLogPage } from './pages/audit/AuditLog.js';
import { TeamPage } from './pages/team/Team.js';
import { SettingsPage } from './pages/settings/Settings.js';

/**
 * ตอนเปิดแอปครั้งแรกยังไม่มี access token ในหน่วยความจำ (ตั้งใจไม่เก็บลง localStorage)
 * จึงลอง /refresh หนึ่งครั้งเพื่อกู้ session จาก httpOnly cookie
 * ระหว่างรอผลต้องไม่เด้งไปหน้า login ไม่งั้นผู้ใช้ที่ล็อกอินอยู่แล้วจะเห็นหน้า login แว้บ ๆ ทุกครั้งที่รีเฟรช
 */
// StrictMode ตอน dev เรียก effect สองรอบ — กันไม่ให้ยิง /refresh ซ้อนกันสองครั้ง
// (ฝั่ง server มีระยะผ่อนผันรองรับอยู่แล้ว แต่ไม่ควรสร้าง request ที่ไม่จำเป็นตั้งแต่ต้น)
let restoreStarted = false;

function useRestoreSession() {
  const { setSession, setReady, ready } = useAuth();
  useEffect(() => {
    if (ready || restoreStarted) return;
    restoreStarted = true;
    void (async () => {
      try {
        const res = await api.raw('/v1/auth/refresh', { method: 'POST', body: {}, anonymous: true });
        if (res.ok) setSession(await res.json());
        else setReady(true);
      } catch {
        setReady(true);
      }
    })();
  }, [ready, setSession, setReady]);
}

function RequireAuth() {
  const { accessToken, ready, tenants } = useAuth();
  const location = useLocation();

  if (!ready) return <div className="grid min-h-screen place-items-center"><Loading label="กำลังเข้าสู่ระบบ…" /></div>;
  if (!accessToken) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  if (!tenants.length) {
    return (
      <div className="grid min-h-screen place-items-center p-6">
        <EmptyState
          icon={<IconBuilding size={22} />} title="บัญชีของคุณยังไม่ได้สังกัดบริษัทใด"
          description="ขอให้ผู้ดูแลของบริษัทส่งคำเชิญมาที่อีเมลของคุณ หรือสมัครสร้างบริษัทใหม่"
          action={<Button onClick={() => { useAuth.getState().clear(); window.location.href = '/login'; }}>ออกจากระบบ</Button>}
        />
      </div>
    );
  }
  return <Outlet />;
}

function RedirectIfAuthed() {
  const { accessToken, ready } = useAuth();
  if (!ready) return <div className="grid min-h-screen place-items-center"><Loading label="" /></div>;
  return accessToken ? <Navigate to="/" replace /> : <Outlet />;
}

export function App() {
  useRestoreSession();

  // ผลิตภัณฑ์ใช้ Light theme เท่านั้น ล้างค่าธีมเดิมเพื่อรองรับผู้ใช้ที่เคยเลือก dark mode
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    localStorage.removeItem('blogaris.theme');
  }, []);

  return (
    <Routes>
      <Route element={<RedirectIfAuthed />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      </Route>

      {/* เข้าถึงได้เสมอ — ลิงก์จากอีเมล */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/invite/:token" element={<AcceptInvitePage />} />

      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route index element={<DashboardPage />} />
          <Route path="blogs" element={<BlogListPage />} />
          <Route path="blogs/:id" element={<BlogDetailPage />} />
          <Route path="blogs/:blogId/posts/:postId" element={<PostEditorPage />} />
          <Route path="widgets" element={<WidgetListPage />} />
          <Route path="widgets/:id" element={<WidgetBuilderPage />} />
          <Route path="media" element={<MediaLibraryPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="audit" element={<AuditLogPage />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={
            <EmptyState icon={<IconCompass size={22} />} title="ไม่พบหน้าที่ต้องการ"
                        description="ลิงก์อาจผิดหรือหน้านี้ถูกย้ายไปแล้ว"
                        action={<Button onClick={() => { window.location.href = '/'; }}>กลับหน้าหลัก</Button>} />
          } />
        </Route>
      </Route>
    </Routes>
  );
}
